import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  getFunnelSwitchJournal,
  type OrderSwitchJournal,
  type SwitchBackResponse,
  type SwitchKonflikt,
  type SwitchPlan,
  type SwitchResponse,
  type SwitchSchritt,
  type SwitchVorbedingung,
  switchFunnelBack,
  switchFunnelOrderUrl,
} from './api';
import { NurAdmin, useIstAdmin } from '../../../components/marketingForm';
import { ConfirmDialog, formatBerlin, Loading } from './ui';

/**
 * Reiter „Live schalten" (Spec § 6): die publizierte Bestellseite wird `order_url` des Webinars.
 *
 * - Vorbedingungen kommen vom Server (Trockenlauf `dryRun: true`, schreibt nichts) und werden beim
 *   echten Lauf INNERHALB der Verweis-Sperre erneut geprueft — die UI spiegelt nur.
 * - Journal (lp_order_switches): DB-Schritt + Folgeschritte mit Status; ein nicht fertiges Journal
 *   laesst sich idempotent „fortsetzen" (auch ein offenes „zurueck"-Journal: POST /switch mit
 *   fortsetzenId, der Server kennt die Richtung).
 * - Zurueckschalten: erst Trockenlauf → Konflikte (Spalten, die seit dem Umschalten jemand von Hand
 *   geaendert hat) einzeln bestaetigen; nicht bestaetigte bleiben unangetastet.
 * - Der Bericht nennt die verbleibenden LPM-Abhaengigkeiten (bereits erzeugte Nachfass-Kampagnen).
 */

const SCHRITT_FARBE: Record<SwitchSchritt['status'], 'default' | 'success' | 'error' | 'warning'> =
  {
    offen: 'warning',
    ok: 'success',
    fehler: 'error',
    uebersprungen: 'default',
  };
const STATUS_LABEL: Record<OrderSwitchJournal['status'], string> = {
  db_done: 'DB umgestellt, Folgeschritte offen',
  done: 'abgeschlossen',
  failed: 'fehlgeschlagen',
};

const wert = (v: string | number | null | undefined) =>
  v === null || v === undefined || v === '' ? '—' : String(v);

/** Spalte der Bestell-URL im Umschalt-Plan (services/lpFunnel/orderSwitch.ts K_ORDER). */
const K_ORDER = 'adserver_ss_webinars.order_url';

/**
 * Vorbedingungen in Marketing-Worten (Namen aus services/lpFunnel/orderSwitch.ts
 * pruefeVorbedingungen). `null` = nur fuer den Admin (Technik: Journal-Tabelle, Build-Hash,
 * Raum-Kaufweg). Marketing sieht nur, was es selbst erfuellen kann — plus Sperren, bei denen es
 * jemanden fragen muss.
 */
function marketingText(v: SwitchVorbedingung): string | null {
  switch (v.name) {
    case 'oeffentlicher_titel':
      return v.ok
        ? 'Öffentlicher Titel ist gesetzt'
        : `${v.text} — in den Webinar-Grunddaten eintragen`;
    case 'bestellseite_publiziert':
      return v.ok
        ? 'Bestellseite ist veröffentlicht'
        : 'Bestellseite ist noch nicht veröffentlicht — erst die Schritte „Prüfen“ und „Veröffentlichen“';
    case 'bestellseite_live':
      return v.ok
        ? 'Die Bestellseite im Netz ist der zuletzt veröffentlichte Stand'
        : 'Die Bestellseite im Netz zeigt noch einen älteren Stand — bitte noch einmal veröffentlichen';
    case 'kampagne':
      return v.ok ? v.text : 'PL-X-Kampagne fehlt — im Schritt „Einstellungen“ eintragen';
    case 'aufzeichnung_ohne_entwurf':
      // orderSwitch.ts: keine oder unveroeffentlichte Aufzeichnungsseite → ok (nichts zu tun);
      // gesperrt wird nur bei einer VEROEFFENTLICHTEN Seite mit unveroeffentlichten Aenderungen.
      return v.ok
        ? 'Aufzeichnungsseite: keine unveröffentlichten Änderungen — gibt es noch keine Aufzeichnungsseite, ist das in Ordnung'
        : `${v.text.replace(/^Aufzeichnungsseite: /, 'Die Aufzeichnungsseite hat ')} — die Aufzeichnungsseite im Seiten-Editor veröffentlichen (Liste, Spalte „Aufzeichnung“)`;
    case 'kein_offenes_journal':
      return v.ok
        ? null
        : 'Ein früheres Umschalten ist nicht abgeschlossen — bitte den Admin Bescheid geben';
    case 'webinar':
      return v.ok ? null : 'Webinar nicht gefunden — bitte den Admin Bescheid geben';
    default:
      // migration, raum, aenderung und Unbekanntes: Technik bzw. eigene Darstellung.
      return null;
  }
}

/**
 * Ein Feld aus Erfolg ODER Fehlerkoerper: fetchData spreadet den Body, die Route legt das Ergebnis bei
 * 409 unter `details` (routes/lpFunnel/switch.ts) — also `res.details.<feld>`.
 */
function ausAntwort<T>(res: { data?: unknown } & Record<string, unknown>, feld: string): T | null {
  const quellen = [res.data, res.details, res];
  for (const q of quellen) {
    const roh = q && typeof q === 'object' ? (q as Record<string, unknown>)[feld] : undefined;
    if (roh !== undefined && roh !== null) return roh as T;
  }
  return null;
}

/** Vorbedingungen aus Erfolg ODER Fehlerkoerper. */
function vorbedingungenAus(
  res: { data?: SwitchResponse } & Record<string, unknown>,
): SwitchVorbedingung[] | null {
  const roh = ausAntwort<SwitchVorbedingung[]>(res, 'vorbedingungen');
  return Array.isArray(roh) ? roh : null;
}

interface Props {
  webinarId: string;
  /** Nur Anzeige — der Server prueft selbst. */
  orderPublished: boolean;
  onChanged: (label: string, warnings: string[]) => void;
}

export default function SwitchPanel({ webinarId, orderPublished, onChanged }: Props) {
  const [vorbedingungen, setVorbedingungen] = useState<SwitchVorbedingung[] | null>(null);
  const [plan, setPlan] = useState<SwitchPlan | null>(null);
  const [planError, setPlanError] = useState<string | null>(null);
  const [journals, setJournals] = useState<OrderSwitchJournal[] | null>(null);
  const [journalError, setJournalError] = useState<string | null>(null);
  const [busy, setBusy] = useState<'switch' | 'fortsetzen' | 'backDry' | 'back' | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [confirmSwitch, setConfirmSwitch] = useState(false);
  /** Pflicht-Haken „Testbestellung gemacht“ — nur Oberflaeche, der Server prueft das nicht. */
  const [testOk, setTestOk] = useState(false);
  const [backPlan, setBackPlan] = useState<SwitchBackResponse | null>(null);
  const [bestaetigt, setBestaetigt] = useState<string[]>([]);
  const [reload, setReload] = useState(0);

  const load = useCallback(async () => {
    const [plan, journal] = await Promise.all([
      switchFunnelOrderUrl(webinarId, { dryRun: true }),
      getFunnelSwitchJournal(webinarId),
    ]);
    return { plan, journal };
  }, [webinarId]);

  useEffect(() => {
    let cancelled = false;
    setVorbedingungen(null);
    setJournals(null);
    (async () => {
      const { plan, journal } = await load();
      if (cancelled) return;
      const vb = vorbedingungenAus(plan as { data?: SwitchResponse } & Record<string, unknown>);
      setVorbedingungen(vb || []);
      setPlan(
        ausAntwort<SwitchPlan>(plan as { data?: SwitchResponse } & Record<string, unknown>, 'plan'),
      );
      setPlanError(
        plan.success ? null : plan.error || 'Vorbedingungen konnten nicht geprüft werden',
      );
      if (journal.success) {
        setJournals(journal.data || []);
        setJournalError(null);
      } else {
        setJournals([]);
        setJournalError(journal.error || 'Journal konnte nicht geladen werden');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [load, reload]);

  const offen = journals?.find((j) => j.status !== 'done') || null;
  const letzteLive = journals?.find((j) => j.direction === 'live') || null;
  const letzte = journals?.[0] || null;
  const istUmgeschaltet = !!letzte && letzte.direction === 'live';
  const alleOk = !!vorbedingungen && vorbedingungen.length > 0 && vorbedingungen.every((v) => v.ok);
  const istAdmin = useIstAdmin();
  /**
   * Gutzustand: der Server meldet „Nichts umzuschalten — alles zeigt schon hierher“ als nicht
   * erfuellte Vorbedingung `aenderung` (der Knopf bleibt deshalb zu). Das ist KEIN Fehler —
   * gruen und in Worten zeigen, nie rot „fehlt“ (Plan 2026-10-22 A0).
   */
  const nichtsZuTun = !!vorbedingungen?.some((v) => v.name === 'aenderung' && !v.ok);
  const offeneMarketing = (vorbedingungen || [])
    .map((v) => ({ v, text: marketingText(v) }))
    .filter((x): x is { v: SwitchVorbedingung; text: string } => x.text !== null);
  const kaufVorher = plan?.vorzustand?.[K_ORDER];
  const kaufNachher = plan?.nachzustand?.[K_ORDER];
  const weitere = plan ? Object.keys(plan.nachzustand).filter((k) => k !== K_ORDER).length : 0;

  const nachAktion = (label: string, j: OrderSwitchJournal | null) => {
    const hinweise = j?.bericht?.hinweise || [];
    const lpm = j?.bericht?.lpmAbhaengigkeiten || [];
    onChanged(label, [
      ...hinweise,
      ...(lpm.length
        ? [`Weiter auf LPM: ${lpm.length} bereits erzeugte Nachfass-Kampagne(n)`]
        : []),
    ]);
    setReload((n) => n + 1);
  };

  const doSwitch = async (fortsetzenId?: number) => {
    setBusy(fortsetzenId ? 'fortsetzen' : 'switch');
    setActionError(null);
    const res = await switchFunnelOrderUrl(webinarId, fortsetzenId ? { fortsetzenId } : {});
    setBusy(null);
    setConfirmSwitch(false);
    if (!res.success || !res.data) {
      const vb = vorbedingungenAus(res as { data?: SwitchResponse } & Record<string, unknown>);
      if (vb) setVorbedingungen(vb);
      setActionError(res.error || 'Umschalten fehlgeschlagen');
      setReload((n) => n + 1);
      return;
    }
    const j = res.data.journal;
    nachAktion(
      j?.status === 'done'
        ? 'Live geschaltet — die Bestellseite ist jetzt die Bestell-URL des Webinars'
        : `Umschalten: ${j ? STATUS_LABEL[j.status] : 'ohne Journal'} — Journal prüfen und ggf. fortsetzen`,
      j,
    );
  };

  const planBack = async () => {
    setBusy('backDry');
    setActionError(null);
    const res = await switchFunnelBack(webinarId, { dryRun: true });
    setBusy(null);
    if (!res.success || !res.data) {
      setActionError(res.error || 'Zurückschalten konnte nicht geplant werden');
      return;
    }
    setBestaetigt([]);
    setBackPlan(res.data);
  };

  const doBack = async () => {
    setBusy('back');
    setActionError(null);
    const res = await switchFunnelBack(webinarId, { bestaetigteKonflikte: bestaetigt });
    setBusy(null);
    setBackPlan(null);
    if (!res.success || !res.data) {
      setActionError(res.error || 'Zurückschalten fehlgeschlagen');
      // 409: inzwischen neue Konflikte — details.konflikte zeigen und einzeln bestaetigen lassen.
      const konflikte = ausAntwort<SwitchKonflikt[]>(
        res as { data?: SwitchBackResponse } & Record<string, unknown>,
        'konflikte',
      );
      if (Array.isArray(konflikte) && konflikte.length > 0) {
        setBestaetigt([]);
        setBackPlan({ konflikte, journal: null });
        return;
      }
      setReload((n) => n + 1);
      return;
    }
    const rest = res.data.konflikte.filter((k) => !bestaetigt.includes(k.spalte));
    nachAktion(
      `Zurückgeschaltet${rest.length ? ` — ${rest.length} Spalte(n) mit Konflikt unverändert gelassen` : ''}`,
      res.data.journal,
    );
  };

  if (!vorbedingungen || !journals) return <Loading label="Vorbedingungen und Journal laden …" />;

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <Alert severity="info">
        „Live schalten“ macht die eigene Bestellseite zur Bestelladresse des Webinars: der
        Kaufknopf im Webinar-Raum, die Aufzeichnungsseite, Evergreen-Anzeigen und neu erzeugte
        Nachfass-Mails führen dann hierher. Die bisherige Adresse wird gesichert und lässt sich
        zurückschalten. Upsell und Danke verlinkt PL-X — das ist der Schritt „Bei PL-X eintragen“.
      </Alert>
      {nichtsZuTun && (
        <Alert severity="success" data-testid="bereits-live">
          <b>Bereits live — nichts zu tun.</b> Die Bestelladresse des Webinars zeigt schon auf diese
          Bestellseite.
        </Alert>
      )}
      {!orderPublished && (
        <Alert severity="warning">
          Die Bestellseite ist noch nicht veröffentlicht — erst die Schritte „Prüfen“ und
          „Veröffentlichen“.
        </Alert>
      )}
      {!nichtsZuTun && kaufNachher !== undefined && (
        <Paper variant="outlined" sx={{ p: 2 }} data-testid="kaufknopf-vorher-nachher">
          <Typography variant="body2" sx={{ mb: 1 }}>
            <b>Vorher:</b> Die Testbestellung macht der Admin (Till) — Bescheid geben und auf sein
            OK warten, erst dann live schalten.
          </Typography>
          <Typography variant="subtitle2" gutterBottom>
            Was sich beim Klick ändert
          </Typography>
          <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
            Der Kaufknopf führt <b>heute</b> auf: {wert(kaufVorher)}
          </Typography>
          <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
            <b>Nach dem Live schalten</b> auf: {wert(kaufNachher)}
          </Typography>
          {weitere > 0 && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              Dazu werden {weitere} weitere Verweise (Webinar-Raum, Aufzeichnung, Evergreen-Anzeigen)
              auf die Bestellseite umgestellt.
            </Typography>
          )}
        </Paper>
      )}
      {planError && <Alert severity="error">{planError}</Alert>}
      {actionError && <Alert severity="error">{actionError}</Alert>}

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Voraussetzungen
        </Typography>
        {vorbedingungen.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            Keine Angaben vom Server.
          </Typography>
        )}
        <Box display="flex" flexDirection="column" gap={0.5}>
          {offeneMarketing.map(({ v, text }) => (
            <Box key={v.name} display="flex" gap={1} alignItems="flex-start">
              <Chip
                size="small"
                color={v.ok ? 'success' : 'error'}
                label={v.ok ? 'erfüllt' : 'offen'}
                sx={{ minWidth: 64 }}
              />
              <Typography variant="body2">{text}</Typography>
            </Box>
          ))}
        </Box>
        <NurAdmin>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1.5 }}>
            Technische Vorbedingungen (nur Admin):
          </Typography>
          <Box display="flex" flexDirection="column" gap={0.5}>
            {vorbedingungen.map((v) => {
              const gut = v.ok || v.name === 'aenderung';
              return (
                <Box key={v.name} display="flex" gap={1} alignItems="flex-start">
                  <Chip
                    size="small"
                    variant="outlined"
                    color={gut ? 'success' : 'error'}
                    label={v.ok ? 'ok' : v.name === 'aenderung' ? 'nichts zu tun' : 'fehlt'}
                    sx={{ minWidth: 48 }}
                  />
                  <Typography variant="body2">
                    <code>{v.name}</code> {v.text}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </NurAdmin>
        <Box display="flex" gap={1} mt={2} flexWrap="wrap">
          <Button
            variant="contained"
            color="success"
            disabled={!alleOk || !!offen || !!busy || istUmgeschaltet}
            onClick={() => {
              setTestOk(false);
              setConfirmSwitch(true);
            }}
          >
            {busy === 'switch' ? 'Schaltet …' : 'Live schalten'}
          </Button>
          {letzteLive && (
            <Button
              variant="outlined"
              color="warning"
              disabled={!!busy || !!offen}
              onClick={planBack}
            >
              {busy === 'backDry' ? 'Plant …' : 'Zurückschalten …'}
            </Button>
          )}
        </Box>
        {!busy && (istUmgeschaltet || nichtsZuTun || offen || !alleOk) && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
            {istUmgeschaltet || nichtsZuTun
              ? '„Live schalten“ ist aus, weil die Bestellseite schon live ist.'
              : offen
                ? '„Live schalten“ ist aus, weil ein früheres Umschalten nicht abgeschlossen ist — der Admin muss es fortsetzen.'
                : '„Live schalten“ ist aus, bis alle Voraussetzungen oben erfüllt sind.'}
          </Typography>
        )}
      </Paper>

      {journalError && istAdmin && <Alert severity="error">{journalError}</Alert>}
      {journals.length > 0 && istAdmin && (
        <Box display="flex" flexDirection="column" gap={1.5}>
          <Typography variant="subtitle2">Journal (nur Admin)</Typography>
          {journals.map((j) => (
            <JournalView
              key={j.id}
              journal={j}
              onFortsetzen={j.status !== 'done' ? () => doSwitch(j.id) : undefined}
              busy={!!busy}
              fortsetzenBusy={busy === 'fortsetzen'}
            />
          ))}
        </Box>
      )}

      <ConfirmDialog
        open={confirmSwitch}
        title="Bestellseite live schalten?"
        confirmLabel="Live schalten"
        busy={busy === 'switch'}
        confirmDisabled={!testOk}
        onConfirm={() => doSwitch()}
        onCancel={() => setConfirmSwitch(false)}
      >
        <Typography variant="body2" gutterBottom>
          Ab sofort kaufen Raum-Zuschauer, Aufzeichnungs- und Evergreen-Besucher über die eigene
          Bestellseite. Die bisherige Adresse wird gesichert; zurückschalten geht jederzeit.
        </Typography>
        {kaufNachher !== undefined && (
          <Typography variant="body2" gutterBottom sx={{ wordBreak: 'break-all' }}>
            Kaufknopf: {wert(kaufVorher)} → <b>{wert(kaufNachher)}</b>
          </Typography>
        )}
        <Typography variant="body2" gutterBottom>
          Vorher erledigt? Bei PL-X zeigt der Upsell-Prozess der Kampagne auf /angebot/ bzw.
          /danke/ (macht, wer Zugang zu PL-X hat), und eine Testbestellung über die Bestellseite
          lief durch. Die Testbestellung macht der Admin (Till) — Bescheid geben und auf sein OK
          warten, erst dann live schalten.
        </Typography>
        <FormControlLabel
          control={
            <Checkbox
              checked={testOk}
              onChange={(e) => setTestOk(e.target.checked)}
              inputProps={{ 'aria-label': 'Testbestellung bestätigt' }}
            />
          }
          label="Der Admin hat die Testbestellung gemacht und OK gegeben."
        />
        {istAdmin && plan && Object.keys(plan.nachzustand).length > 0 && (
          <Box sx={{ mt: 1.5 }}>
            <Typography variant="body2" gutterBottom>
              <b>Geschrieben wird:</b>
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
              {Object.keys(plan.nachzustand).map((spalte) => (
                <li key={spalte}>
                  <Typography variant="body2" component="span">
                    {spalte}: {wert(plan.vorzustand[spalte])} → {wert(plan.nachzustand[spalte])}
                  </Typography>
                </li>
              ))}
            </Box>
          </Box>
        )}
      </ConfirmDialog>

      <ConfirmDialog
        open={!!backPlan}
        title="Zurückschalten?"
        confirmLabel="Zurückschalten"
        danger
        busy={busy === 'back'}
        onConfirm={doBack}
        onCancel={() => setBackPlan(null)}
      >
        <Typography variant="body2" gutterBottom>
          Stellt die Werte von vor dem Umschalten wieder her — aber nur in Spalten, die seitdem
          niemand geändert hat.
        </Typography>
        {backPlan && backPlan.konflikte.length > 0 && (
          <KonfliktListe
            konflikte={backPlan.konflikte}
            bestaetigt={bestaetigt}
            onChange={setBestaetigt}
          />
        )}
        {backPlan && backPlan.konflikte.length === 0 && (
          <Alert severity="success">
            Keine Konflikte — alle Spalten stehen noch auf dem Umschalt-Wert.
          </Alert>
        )}
      </ConfirmDialog>
    </Box>
  );
}

function KonfliktListe({
  konflikte,
  bestaetigt,
  onChange,
}: {
  konflikte: SwitchKonflikt[];
  bestaetigt: string[];
  onChange: (next: string[]) => void;
}) {
  return (
    <Box>
      <Alert severity="warning" sx={{ mb: 1 }}>
        {konflikte.length} Spalte(n) wurden seit dem Umschalten von Hand geändert. Ohne Haken
        bleiben sie, wie sie sind; mit Haken werden sie trotzdem auf den alten Wert gesetzt.
      </Alert>
      {konflikte.map((k) => (
        <Paper key={k.spalte} variant="outlined" sx={{ p: 1, mb: 1 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={bestaetigt.includes(k.spalte)}
                onChange={(e) =>
                  onChange(
                    e.target.checked
                      ? [...bestaetigt, k.spalte]
                      : bestaetigt.filter((s) => s !== k.spalte),
                  )
                }
              />
            }
            label={
              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                {k.spalte}
              </Typography>
            }
          />
          <Typography variant="caption" display="block" sx={{ wordBreak: 'break-all' }}>
            nach dem Umschalten: {wert(k.nachUmschalten)}
          </Typography>
          <Typography variant="caption" display="block" sx={{ wordBreak: 'break-all' }}>
            heute: {wert(k.aktuell)}
          </Typography>
        </Paper>
      ))}
    </Box>
  );
}

function JournalView({
  journal: j,
  onFortsetzen,
  busy,
  fortsetzenBusy,
}: {
  journal: OrderSwitchJournal;
  onFortsetzen?: () => void;
  busy: boolean;
  fortsetzenBusy: boolean;
}) {
  const spalten = Array.from(
    new Set([...Object.keys(j.vorzustand || {}), ...Object.keys(j.nachzustand || {})]),
  );
  const lpm = j.bericht?.lpmAbhaengigkeiten || [];
  return (
    <Paper variant="outlined" sx={{ p: 1.5 }}>
      <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
        <Typography variant="body2" fontWeight={600}>
          #{j.id} {j.direction === 'live' ? 'Live geschaltet' : 'Zurückgeschaltet'}
        </Typography>
        <Chip
          size="small"
          label={STATUS_LABEL[j.status] || j.status}
          color={j.status === 'done' ? 'success' : j.status === 'failed' ? 'error' : 'warning'}
        />
        <Typography variant="caption" color="text.secondary">
          {formatBerlin(j.createdAt)} · {j.createdBy}
          {j.updatedAt && j.updatedAt !== j.createdAt
            ? ` · zuletzt ${formatBerlin(j.updatedAt)}`
            : ''}
        </Typography>
        <Box flex={1} />
        {onFortsetzen && (
          <Button size="small" variant="contained" onClick={onFortsetzen} disabled={busy}>
            {fortsetzenBusy ? 'Setzt fort …' : 'Fortsetzen'}
          </Button>
        )}
      </Box>

      {j.schritte.length > 0 && (
        <Box display="flex" flexDirection="column" gap={0.25} mt={1}>
          {j.schritte.map((s) => (
            <Box key={s.name} display="flex" gap={1} alignItems="center">
              <Chip
                size="small"
                variant="outlined"
                color={SCHRITT_FARBE[s.status]}
                label={s.status}
                sx={{ minWidth: 90 }}
              />
              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                {s.name}
              </Typography>
              {s.detail && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ wordBreak: 'break-all' }}
                >
                  {s.detail}
                </Typography>
              )}
              {s.at && (
                <Typography variant="caption" color="text.secondary">
                  {formatBerlin(s.at)}
                </Typography>
              )}
            </Box>
          ))}
        </Box>
      )}

      {j.bericht && (
        <Box mt={1}>
          {j.bericht.offerMode && (
            <Typography variant="body2">
              Raum-Kaufweg: <b>{j.bericht.offerMode}</b>
              {j.bericht.offerMode === 'embed' ? ' (Kauf im Raum, bleibt so)' : ''}
            </Typography>
          )}
          {lpm.length > 0 && (
            <Alert severity="warning" sx={{ mt: 0.5 }}>
              <b>Verbleibende LPM-Abhängigkeiten ({lpm.length})</b> — bereits erzeugte
              Nachfass-Kampagnen tragen die alte URL; die LPM-Seiten müssen online bleiben, bis sie
              ausgelaufen sind.
              <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
                {lpm.map((x) => (
                  <li key={x}>
                    <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                      {x}
                    </Typography>
                  </li>
                ))}
              </Box>
            </Alert>
          )}
          {j.bericht.hinweise.length > 0 && (
            <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
              {j.bericht.hinweise.map((h) => (
                <li key={h}>
                  <Typography variant="body2">{h}</Typography>
                </li>
              ))}
            </Box>
          )}
        </Box>
      )}

      {spalten.length > 0 && (
        <Table size="small" sx={{ mt: 1 }}>
          <TableHead>
            <TableRow>
              {['Spalte', 'vorher', 'nachher'].map((h) => (
                <TableCell
                  key={h}
                  sx={{ backgroundColor: '#e0e0e0 !important', color: 'black', fontWeight: 'bold' }}
                >
                  {h}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {spalten.map((s) => (
              <TableRow key={s}>
                <TableCell sx={{ color: 'black', fontFamily: 'monospace', fontSize: 12 }}>
                  {s}
                </TableCell>
                <TableCell sx={{ color: 'black', fontSize: 12, wordBreak: 'break-all' }}>
                  {wert(j.vorzustand?.[s])}
                </TableCell>
                <TableCell sx={{ color: 'black', fontSize: 12, wordBreak: 'break-all' }}>
                  {wert(j.nachzustand?.[s])}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Paper>
  );
}
